import React, { useState } from "react";
import { type MRT_ColumnDef, } from "material-react-table";
import dayjs from "dayjs";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	MenuItem,
	TextField,
	Alert,
	Tooltip,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
	transmissionProjectTypes,
	energyProjectTypes,
	fundingList,
	newProject,
	type Project,
	statusList,
	forecastDetachedHouseProjectTypes
} from "../TableData";
import RadioGroups from "./RadioGroups";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { calculateProcessingPath } from "../../../services/utils/excelUtils";

interface CreateModalProps {
	columns: MRT_ColumnDef<Project>[];
	onClose: () => void;
	onSubmit: (values: Project) => void;
	open: boolean;
	AllLists: any;
	projectList: any;
	Juri: string;
}

export const CreateNewAccountModal = ({
	open,
	onClose,
	onSubmit,
	AllLists,
	Juri,
	projectList
}: CreateModalProps) => {
	const source = Juri == "FED" ? "Federal" : Juri;
	const currentYear = dayjs().year();
	const today = dayjs().toISOString();
	const [values, setValues] = useState<Project>({ ...newProject, ["Source"]: source });
	const [forecast, setForecast] = useState<string>("no");
	const [ongoing, setOngoing] = useState<string>("no");


	const investmentRequired = (values: Project): boolean => {
		// processing path 3  and 4
		if (forecast === "yes" && [...energyProjectTypes, ...forecastDetachedHouseProjectTypes, transmissionProjectTypes[1], transmissionProjectTypes[2]].indexOf(values.RawProjectType) !== -1)
			return false;
		// processing path 2
		if (forecast === "no" && ongoing === "no" && energyProjectTypes.indexOf(values.RawProjectType) !== -1) {
			return false;
		}

		return true
	}

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		//put your validation logic here
		event.preventDefault();
		if (!validateInput(values, projectList, forecast)) {
			// calculate processing path based on forecast, ongoing and project type
			const processingPath = calculateProcessingPath(forecast, ongoing, values);
			onSubmit({ ...values, ["ProcessingPath"]: processingPath });
			// reset create project when model is closed
			setValues({ ...newProject, ["Source"]: source });
			setForecast("no");
			setOngoing("no");
			onClose();
		}

	};
	const minConstructionDate = dayjs('2015', 'YYYY')
	const maxConstructionDate = dayjs('2040', 'YYYY')

	return (
		<Dialog
			open={open}
			fullWidth={true}
			maxWidth="lg">
			<form onSubmit={handleSubmit}>
				<DialogTitle id="create-dialog-title">
					{"Scenario project creation"}
				</DialogTitle>
				<DialogContent>
					<Box p={2}>
						{/* RadioGroups are removed or commented out */}
						{/*
						<RadioGroups
							groupLabel="Is your project on ISP or ABS forecast?"
							groupValue={forecast}
							handleChange={(e) => {
								if (e.target.value === "yes") {
									// set funding mode to Portfolio if forecast is yes
									// set construction start and end date to 1st July of current year and next year if forecast is yes
									setValues({
										...values, ["FundingMode"]: "Portfolio",
										["StartDateConstruction"]: dayjs(`${currentYear}-07-02`).toISOString(),
										["EndDateConstruction"]: dayjs(`${currentYear + 1}-07-01`).toISOString()
									});
								} else {
									setValues({ ...values, ["FundingMode"]: "", ["StartDateConstruction"]: `${today}`, ["EndDateConstruction"]: `${today}` });

								}
								setForecast(e.target.value)
							}}
							spacing={3.5}
							tooltipTitle="Are you providing data based on ISP or ABS forecast? If you are not sure, please select No."
						/>
						<RadioGroups
							groupLabel="Is your project an on-going Programme?"
							groupValue={ongoing}
							handleChange={(e) => { setOngoing(e.target.value) }}
							spacing={2}
							tooltipTitle="Select No if your project is a discrete construction project"
						/>
						*/}
					</Box>

					<Box p={2} display="flex" className="hide-asterisk">
						<Grid container spacing={2}>
							<Grid item xs={5.6}>
								<TextField
									fullWidth
									key="SourceID"
									label="Original project ID"
									name="SourceID"
									value={values.SourceID}
									onChange={(e) =>
										setValues({ ...values, [e.target.name]: e.target.value })
									}
									required
								/>
							</Grid>
							{/*<Grid item xs={0.4}>	<Tooltip title="place holder for instructions text"><HelpOutlineIcon /></Tooltip></Grid>*/}
							<Grid item xs={5.6}>
								<TextField
									fullWidth
									key="State"
									label="State"
									name="State"
									value={values.State}
									onChange={(e) =>
										setValues({
											...values,
											[e.target.name]: e.target.value,
											["LocationID"]: ""
										})
									}
									select
									required
								>
									{AllLists?.executeGetState.map((option) => (
										<MenuItem key={option.State} value={option.State}>
											{option.State}
										</MenuItem>
									))}
								</TextField>
							</Grid>
							{/*<Grid item xs={0.4}>	<Tooltip title="place holder for instructions text"><HelpOutlineIcon /></Tooltip></Grid>*/}
							<Grid item xs={5.6}>
								<TextField
									fullWidth
									key="ProjectName"
									label="Project name"
									name="ProjectName"
									value={values.ProjectName}
									onChange={(e) =>
										setValues({ ...values, [e.target.name]: e.target.value })
									}
									required
								/>
							</Grid>
							{/*<Grid item xs={0.4}>	<Tooltip title="place holder for instructions text"><HelpOutlineIcon /></Tooltip></Grid>*/}
							<Grid item xs={5.6}>
								<TextField
									fullWidth
									key="Source"
									label="Source"
									name="Source"
									value={values.Source}
									disabled
								/>
							</Grid>
							{/*<Grid item xs={0.4}>	<Tooltip title="place holder for instructions text"><HelpOutlineIcon /></Tooltip></Grid>*/}
							<Grid item xs={5.6}>
								<TextField
									fullWidth
									key="LocationID"
									label="Location"
									name="LocationID"
									select
									value={values.LocationID}
									onChange={(e) =>
										setValues({ ...values, [e.target.name]: e.target.value })
									}
									required={values.State != 'AUS'}
								>
									{AllLists?.executeGetLocationList.filter(
										item => item.State == values.State || values.State == '' || values.State == 'AUS'
									).map((option) => (
										<MenuItem key={option.LocationID} value={option.LocationID}>
											{option.LocationName}
										</MenuItem>
									))}
								</TextField>
							</Grid>
							{/*<Grid item xs={0.4}><Tooltip title="place holder for instructions text"><HelpOutlineIcon /></Tooltip></Grid>*/}
							<Grid item xs={5.6}>
								<TextField
									fullWidth
									key="RawProjectType"
									label="Project type"
									name="RawProjectType"
									select
									value={values.RawProjectType}
									onChange={(e) =>
										// clear display unit when select different project types
										setValues({ ...values, [e.target.name]: e.target.value, ["MW"]: 0, ["km"]: 0 })
									}
									required
								>
									{AllLists?.executeGetRawProjectTypes.map((option) => {
										if (forecast === "yes" && [...transmissionProjectTypes, ...energyProjectTypes, ...forecastDetachedHouseProjectTypes].indexOf(option.RawProjectType) !== -1) {
											return <MenuItem key={option.RawProjectType} value={option.RawProjectType}>
												{option.RawProjectType}
											</MenuItem>
										}
										if (forecast === "no" && ongoing === "yes" && [...transmissionProjectTypes, ...energyProjectTypes, ...forecastDetachedHouseProjectTypes].indexOf(option.RawProjectType) === -1) {
											return <MenuItem key={option.RawProjectType} value={option.RawProjectType}>
												{option.RawProjectType}
											</MenuItem>
										}
										if (forecast === "no" && ongoing === "no" && [...transmissionProjectTypes, ...forecastDetachedHouseProjectTypes].indexOf(option.RawProjectType) === -1) {
											return <MenuItem key={option.RawProjectType} value={option.RawProjectType}>
												{option.RawProjectType}
											</MenuItem>
										}
									})}
								</TextField>
							</Grid>
							{/* <Grid item xs={0.4}>	<Tooltip title="place holder for instructions text"><HelpOutlineIcon /></Tooltip></Grid>*/}
							<Grid item xs={5.6}>
								<TextField
									fullWidth
									key="Investment"
									label="Investment ($millions)"
									name="Investment"
									value={values.Investment}
									inputProps={{ inputMode: "decimal", pattern: "[0-9]*.[0-9]*" }}
									onChange={(e) =>
										setValues({ ...values, [e.target.name]: (e.target.value) })
									}
									error={isNaN(values.Investment)}
									required={investmentRequired(values)}
								/>
							</Grid>
							{/* <Grid item xs={0.4}>	<Tooltip title="place holder for instructions text"><HelpOutlineIcon /></Tooltip></Grid>*/}
							<Grid item xs={5.6}>
								<TextField
									fullWidth
									key="FundingMode"
									label="Funding mode"
									name="FundingMode"
									value={values.FundingMode}
									onChange={(e) =>
										setValues({ ...values, [e.target.name]: e.target.value })
									}
									select
									disabled={forecast === "yes" ? true : false}
									required
								>
									{fundingList.map((option) => (
										<MenuItem key={option.value} value={option.text}>
											{option.text}
										</MenuItem>
									))}
								</TextField>
							</Grid>
							{/* <Grid item xs={0.4}>	<Tooltip title="place holder for instructions text"><HelpOutlineIcon /></Tooltip></Grid>*/}
							<Grid item xs={5.6}>
								<TextField
									fullWidth
									key="ProjectStage"
									label="Project latest status"
									name="ProjectStage"
									select
									value={values.ProjectStage}
									onChange={(e) =>
										setValues({ ...values, [e.target.name]: e.target.value })
									}
									required={forecast === "yes" ? false : true}
								>
									{statusList.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.text}
										</MenuItem>
									))}
								</TextField>
							</Grid>
							{/* <Grid item xs={0.4}>	<Tooltip title="place holder for instructions text"><HelpOutlineIcon /></Tooltip></Grid>*/}
							<Grid item xs={5.6}>
								{energyProjectTypes.indexOf(values.RawProjectType) > -1 &&
									<TextField
										fullWidth
										key="MW"
										label="Megawatts"
										name="MW"
										value={values.MW}
										error={(isNaN(values.MW) || values.MW <= 0)}
										inputProps={{
											inputMode: "decimal",
											pattern: "[0-9]*.[0-9]*"
										}}
										onChange={(e) =>
											setValues({ ...values, [e.target.name]: (e.target.value) })
										}
										required />
								}
								{["Transmission Line: Double Circuit",
									"Transmission Line: Single Circuit"].indexOf(values.RawProjectType) > -1 &&
									<TextField
										fullWidth
										key="KM"
										label="Kilometers"
										name="km"
										value={values.km}
										error={(isNaN(values.km) || values.km <= 0)}
										inputProps={{
											inputMode: "decimal",
											pattern: "[0-9]*.[0-9]*"
										}}
										onChange={(e) =>
											setValues({ ...values, [e.target.name]: (e.target.value) })
										}
										required
									/>}
								{forecastDetachedHouseProjectTypes.indexOf(values.RawProjectType) > -1 && <TextField
									fullWidth
									key="M2"
									label="Metre square"
									name="M2"
									value={values.M2}
									error={(isNaN(values.M2) || values.M2 <= 0)}
									inputProps={{
										inputMode: "decimal",
										pattern: "[0-9]*.[0-9]*"
									}}
									onChange={(e) =>
										setValues({ ...values, [e.target.name]: (e.target.value) })
									}
									required
								/>}
							</Grid>
							{energyProjectTypes.indexOf(values.RawProjectType) > -1 }
							{energyProjectTypes.indexOf(values.RawProjectType) > -1 }
							{/*{energyProjectTypes.indexOf(values.RawProjectType) > -1 && <Grid item xs={0.4}>	<Tooltip title="place holder for instructions text"><HelpOutlineIcon /></Tooltip></Grid>}
							{energyProjectTypes.indexOf(values.RawProjectType) > -1 && <Grid item xs={0.4}>	<Tooltip title="place holder for instructions text"><HelpOutlineIcon /></Tooltip></Grid>} */}
						</Grid>
					</Box>
					<Box p={2}>
						<Grid container spacing={2}>
							<Grid item xs={5.6}>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DatePicker
										label="Construction start date"
										onChange={(date) => {
											// Check if date is not null before accessing its format method
											if (date) {
												setValues({
													...values,
													"StartDateConstruction": dayjs(date).toISOString()
												});
											}
										}}
										timezone="UTC"
										value={dayjs(values.StartDateConstruction)}
										slotProps={{
											textField: {
												helperText: "Select a date",
												fullWidth: true,
												error: dayjs(values.EndDateConstruction).isBefore(dayjs(values.StartDateConstruction))
											},
										}}
										format="DD/MM/YYYY"
										minDate={minConstructionDate}
										maxDate={maxConstructionDate}
									/>
								</LocalizationProvider>
							</Grid>
						{/*	<Grid item xs={0.4}>	<Tooltip title="place holder for instructions text"><HelpOutlineIcon /></Tooltip></Grid>*/}
							<Grid item xs={5.6}>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DatePicker
										label="Construction end date"
										onChange={(date) => {
											// Check if date is not null before accessing its format method
											if (date) {
												setValues({
													...values,
													"EndDateConstruction": dayjs(date).toISOString()
												});
											}
										}}
										timezone="UTC"
										value={dayjs(values.EndDateConstruction)}
										slotProps={{
											textField: {
												helperText: "Select a date",
												fullWidth: true,
												error: dayjs(values.EndDateConstruction).isBefore(dayjs(values.StartDateConstruction))
											},
										}}
										format="DD/MM/YYYY"
										minDate={minConstructionDate}
										maxDate={maxConstructionDate}

									/>
								</LocalizationProvider>
							</Grid>
						{/*	<Grid item xs={0.4}>	<Tooltip title="place holder for instructions text"><HelpOutlineIcon /></Tooltip></Grid>*/}
						</Grid>
					</Box>
					{validateInput(values, projectList, forecast) && <Alert severity="error">{validateInput(values, projectList, forecast)}</Alert>}
				</DialogContent>
				<DialogActions sx={{ p: "1.25rem" }}>
					<Button onClick={() => {
						onClose();
						setValues({ ...newProject, ["Source"]: source });
						setForecast("no"); // set to default
						setOngoing("no"); // set to default
					}
					}>Cancel</Button>
					<Button color="secondary" variant="contained" type="submit">
						Create
					</Button>
				</DialogActions>
			</form>
		</Dialog >
	);
};
export function validateInput(values, projectList, forecast) {
	if (projectList?.executeGetScenarioProjectList.filter((item) => item['ProjectName'] == values.ProjectName).length > 0) {
		return "Project name already exists. Please provide an unique name."
	}

	if (values.ProjectName.length > 255) {
		return "Project name needs to be less than 255 characters."
	}
	if (isNaN(values.Investment)) {
		return "Please provide a valid non-zero investment amount."
	}
	if ((isNaN(values.MW) || values.MW <= 0) && (energyProjectTypes.indexOf(values.RawProjectType) > -1)) {
		return "Please provide a valid non-zero Megawatts amount."
	}
	if (isNaN(values.M2) || values.M2 <= 0 && (forecastDetachedHouseProjectTypes.indexOf(values.RawProjectType) > -1)) {
		return "Please provide a valid non-zero Metre square amount."
	}
	if ((isNaN(values.km) || values.km <= 0) && (["Transmission Line: Double Circuit",
		"Transmission Line: Single Circuit"].indexOf(values.RawProjectType) > -1)) {
		return "Please provide a valid non-zero Kilometers amount."
	}
	if (dayjs(values.EndDateConstruction).isBefore(dayjs(values.StartDateConstruction))) {
		return "Invalid construction date range, Construction start date should be before Construction end date."
	}
	if (forecast === "yes" && dayjs(values.StartDateConstruction)?.month() !== 7 && dayjs(values.StartDateConstruction)?.date() !== 2) {
		return "Construction start date should be the start of the financial year."
	}
	if (forecast === "yes" && dayjs(values.EndDateConstruction)?.month() !== 6 && dayjs(values.EndDateConstruction)?.date() !== 1) {
		return "Construction end date should be the end of the financial year."
	}
	return ""
}
